import { Badge, Button, Card, Col, Flex, Row, Spin, Tabs, Typography } from "antd";
import { motion } from "framer-motion";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useEffect, useState } from "react";
import { periodAcitivityChartOptions, periodActivityPassageChartOptions } from "./helpers";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { Mail, Users, Video } from "lucide-react";
import { useGetExamInstanceMonitor, useGetExamPeriodById } from "@hooks";
import { ExamPeriodMonitorTable } from "@components";
import { ProctoringWall } from "@/components/proctoring/ProctoringWall";
import { ProctorAssignmentsDrawer } from "@/components/proctoring/ProctorAssignmentsDrawer";
import { InviteAssessorsModal } from "@/components/proctoring/InviteAssessorsModal";

export const ExamPeriodMonitoringPage = () => {
 
  const { t } = useTranslation();
  
  const { id } = useParams();
  const { data: exam, isLoading } = useGetExamPeriodById(id ? id : "");

  const { data: monitorData, isLoading: monitorLoading } =
    useGetExamInstanceMonitor(id ? id : "");

  const [chartOptions, setChartOptions] = useState<ApexOptions>(periodAcitivityChartOptions(t, monitorData));
  const [chartPassageOptions, setChartPassageOptions] = useState<ApexOptions>(periodActivityPassageChartOptions(t, monitorData));
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "video" ? "video" : "overview";
  const [assignOpen, setAssignOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    if (monitorData) {
      setChartOptions(periodAcitivityChartOptions(t, monitorData))
      setChartPassageOptions(periodActivityPassageChartOptions(t, monitorData))
    }
  }, [monitorData]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      <Spin spinning={isLoading}>
        <div
          style={{ display: "flex", flexDirection: "row", marginBottom: 20 }}
        >
          <div
            style={{
              width: 3,
              backgroundColor: "#F1B80E",
              borderRadius: 100,
              marginRight: 12,
            }}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t("pages.activity.name")}
          </Typography.Title>
        </div>
        {(() => {
          const overview = (
            <Row gutter={16}>
              <Col span={18}>
                <ExamPeriodMonitorTable exam={exam} monitorData={monitorData} isLoading={monitorLoading}/>
              </Col>
              <Col span={6}>
                <Card title={t("pages.activity.stats")}>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <ReactApexChart
                      options={chartOptions}
                      series={chartOptions.series}
                      type="donut"
                      width={300}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <ReactApexChart
                      options={chartPassageOptions}
                      series={chartPassageOptions.series}
                      type="donut"
                      width={300}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          );
          if (!exam?.videoProctoringEnabled || !id) return overview;
          // The wall only connects to LiveKit while its tab is open.
          return (
            <>
            <Tabs
              activeKey={tab}
              destroyInactiveTabPane
              onChange={(key) => setSearchParams(key === "video" ? { tab: "video" } : {}, { replace: true })}
              tabBarExtraContent={
                <Flex gap={8}>
                  <Button icon={<Users size={16} />} onClick={() => setAssignOpen(true)} data-testid="monitor-assignments">
                    {t("proctoring.assignments")}
                  </Button>
                  <Button icon={<Mail size={16} />} onClick={() => setInviteOpen(true)} data-testid="monitor-invite-assessors">
                    {t("proctoring.invite")}
                  </Button>
                </Flex>
              }
              items={[
                { key: "overview", label: t("proctoring.tabOverview"), children: overview },
                {
                  key: "video",
                  label: (
                    <span data-testid="monitor-video-tab">
                      <Badge status="processing" style={{ marginRight: 6 }} />
                      <Video size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                      {t("proctoring.menu")}
                    </span>
                  ),
                  children: <ProctoringWall termId={id} embedded />,
                },
              ]}
            />
            <ProctorAssignmentsDrawer examTermId={id} open={assignOpen} onClose={() => setAssignOpen(false)} />
            <InviteAssessorsModal termId={id} open={inviteOpen} onClose={() => setInviteOpen(false)} />
            </>
          );
        })()}
      </Spin>
    </motion.div>
  );
};
